import { type CSSProperties, useEffect, useRef, useState } from "react";
import "./TimelinePage.css";
import rakkolevaImage from "../assets/rakkoleva.jpg";
import ruutPelagoImage from "../assets/ruut_pelago.jpg";

type TimelineBackCover = {
	src: string;
	alt: string;
	objectPosition?: string;
};

type TimelineEvent = {
	date: string;
	title: string;
	description: string;
	accent: string;
	dateAccent?: string;
	backCover?: TimelineBackCover;
};

const timelineEvents: TimelineEvent[] = [
	{
		date: "Eka swaippaus",
		title: "Uulalaa",
		description:
			"No siitä se sitten lähti. Ranskalaiset vietiin ja shakissa mittelöitiin.",
		accent: "#ff8e9e",
	},
	{
		date: "KKK",
		title: "Kello kymmenen kakka",
		description:
			"Kuinka voisinkaan unohtaa. Laskiaiskroisantti, kello kymmenen kakka, ja se hymy.",
		accent: "#f7a94b",
	},
	{
		date: "Se on sitten menoa",
		title: "Haluuxä alkaa",
		description:
			"Tästä ei sitten enää ole takaisin paluuta. Paree olla varma nyt.",
		accent: "#ff6f91",
	},
	{
		date: "Munkkiniemi calling",
		title: "Sitä hyvää elämää",
		description: "Munkkiniemi, kahvia ja pullaa.",
		accent: "#8ec5ff",
	},
	{
		date: "Pelago",
		title: "Uusi rakas perheenjäsen",
		description: "Pelago ja Ruut ne yhteen sopii.",
		accent: "#9adf9f",
		backCover: {
			src: ruutPelagoImage,
			alt: "Ruut pyöräilemässä Pelagolla",
		},
	},
	{
		date: "Jäätelökesä 2026",
		title: "Minä, Ruut ja jäätelö",
		description:
			"Kolmiodraama parhaimmillaan. Kuka syö viimeisen lusikallisen? Kuka jaksaa odottaa sulamista?",
		accent: "#44919a",
	},
	{
		date: "Yöllä",
		title: "El katastrofi",
		description:
			"Lopun alku. Puhelin karkuteillä. Yksinäinen Mashirossa. Toukon kiitoliito. Etsi ja löydä. Kalasatama kutsuu.",
		accent: "#fffb00",
		dateAccent: "#d0cc00",
	},
	{
		date: "1.9.2026",
		title: "Kihlattua mie metsästän",
		description: "Uusi ja kiiltävä pallo jalassa.",
		accent: "#ffb6c1",
		dateAccent: "#ffb6c1",
		backCover: {
			src: rakkolevaImage,
			alt: "Rakkolevä sormus",
			objectPosition: "center",
		},
	},
];

type TimelinePageProps = {
	onGoHome: () => void;
};

function TimelinePage({ onGoHome }: TimelinePageProps) {
	const [flippedItems, setFlippedItems] = useState<number[]>([]);
	const [expandedCover, setExpandedCover] = useState<TimelineBackCover | null>(
		null,
	);
	const coverDialogRef = useRef<HTMLDialogElement>(null);

	useEffect(() => {
		const dialog = coverDialogRef.current;

		if (!dialog) {
			return;
		}

		if (expandedCover && !dialog.open) {
			dialog.showModal();
		} else if (!expandedCover && dialog.open) {
			dialog.close();
		}
	}, [expandedCover]);

	const toggleItem = (index: number) => {
		setFlippedItems((previousItems) =>
			previousItems.includes(index)
				? previousItems.filter((itemIndex) => itemIndex !== index)
				: [...previousItems, index],
		);
	};

	const closeExpandedCover = () => {
		setExpandedCover(null);
	};

	return (
		<section className="page page-timeline">
			<div className="timeline-shell">
				<button className="back-link" type="button" onClick={onGoHome}>
					Takaisin
				</button>
				<header className="timeline-hero">
					<p className="eyebrow">Parisuhde almanakka</p>
					<h1>Toukoruu mikset sä jo tuu</h1>
					<p className="timeline-intro">
						Höpsötyksiä, höpötyksiä sekä höpönassuja.
					</p>
				</header>
				<div className="timeline-track">
					<div className="timeline-line" aria-hidden="true" />
					<ol className="timeline-list">
						{timelineEvents.map((event, index) => {
							const isFlipped = flippedItems.includes(index);
							const hasBackCover = Boolean(event.backCover);

							return (
								<li
									key={event.title}
									className={`timeline-item ${index % 2 === 0 ? "left" : "right"}`}
									style={
										{
											"--event-accent": event.accent,
											"--date-accent": event.dateAccent ?? event.accent,
										} as CSSProperties
									}
								>
									<div className="timeline-dot" aria-hidden="true" />
									<article
										onClick={hasBackCover ? () => toggleItem(index) : undefined}
										onKeyDown={
											hasBackCover
												? (keyboardEvent) => {
														if (
															keyboardEvent.target !==
															keyboardEvent.currentTarget
														) {
															return;
														}

														if (
															keyboardEvent.key === "Enter" ||
															keyboardEvent.key === " "
														) {
															keyboardEvent.preventDefault();
															toggleItem(index);
														}
													}
												: undefined
										}
										className={`timeline-card ${hasBackCover ? "has-back-cover" : ""}`}
										role={hasBackCover ? "button" : undefined}
										tabIndex={hasBackCover ? 0 : undefined}
										aria-label={
											hasBackCover ? `Käännä ${event.title} -kortti` : undefined
										}
										aria-pressed={hasBackCover ? isFlipped : undefined}
									>
										<div
											className={`flip-card-inner ${isFlipped ? "flipped" : ""}`}
										>
											<div className="timeline-card-panel flip-card-front">
												<p className="timeline-date">{event.date}</p>
												<h2>{event.title}</h2>
												<p>{event.description}</p>
											</div>

											{event.backCover && (
												<div className="timeline-card-panel flip-card-back">
													<div className="timeline-cover-frame">
														<img
															className="timeline-cover-image"
															src={event.backCover.src}
															alt={event.backCover.alt}
															style={{
																objectPosition:
																	event.backCover.objectPosition ?? undefined,
															}}
														/>
													</div>
													<button
														className="timeline-cover-expand"
														type="button"
														onClick={(clickEvent) => {
															clickEvent.stopPropagation();
															setExpandedCover(event.backCover ?? null);
														}}
														aria-label={`Näytä ${event.title} koko näytöllä`}
													>
														<span aria-hidden="true">⛶</span>
													</button>
												</div>
											)}
										</div>
									</article>
								</li>
							);
						})}
					</ol>
				</div>
			</div>
			<dialog
				ref={coverDialogRef}
				className="timeline-cover-dialog"
				onCancel={closeExpandedCover}
				onClose={() => setExpandedCover(null)}
				onClick={(clickEvent) => {
					if (clickEvent.target === clickEvent.currentTarget) {
						closeExpandedCover();
					}
				}}
				aria-label="Kuva koko näytöllä"
			>
				<div
					className="timeline-cover-dialog-content"
					onClick={(clickEvent) => {
						if (clickEvent.target === clickEvent.currentTarget) {
							closeExpandedCover();
						}
					}}
				>
					{expandedCover && (
						<img
							className="timeline-fullscreen-image"
							src={expandedCover.src}
							alt={expandedCover.alt}
						/>
					)}
					<button
						className="timeline-cover-close"
						type="button"
						onClick={closeExpandedCover}
						aria-label="Sulje koko näytön kuva"
					>
						<span aria-hidden="true">×</span>
					</button>
				</div>
			</dialog>
		</section>
	);
}

export default TimelinePage;
